import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import TooltipIcon from '../../generic/TooltipIcon';
import { useVulnerabilityPreviewURL } from '../hooks/useVulnerabilityPreviewURL';
import { useLoadPreview } from '../preview/hooks';

import previewStyles from '../preview/Preview.module.less';

/**
 * Renders the preview iframe for the vulnerability (security) report.
 * Reuses styles from the shared Preview component. Format is always PDF.
 *
 * @deprecated The Security/Vulnerability report is a legacy surface gated by
 * the `SECURITY_PDF_REPORTS` feature flag. This report format is no longer
 * supported and is being phased out — do not add new functionality to this
 * component or extend its behaviour. New reporting work should target the
 * Remediation Guidance report (or successor) instead.
 */
export const VulnerabilityPreview: VoidFunctionComponent = () => {
  const previewURL = useVulnerabilityPreviewURL();
  const { cacheBustedURL, debouncedPreviewURL, isLoading, isPending, onIframeLoad } = useLoadPreview(previewURL);

  return (
    <div className={previewStyles.preview}>
      <header className={previewStyles.header}>
        <span className={previewStyles.headerTitle}>
          Preview
        </span>
        <TooltipIcon
          id="vulnerability-preview-tooltip"
          icon={icons.circleQuestion}
          tooltip="Previews use sample data and only show a limited number of vulnerabilities"
        />
      </header>
      {isLoading && (
        <div className={previewStyles.loading}>
          <Loader />
          <span className={previewStyles.loadingText}>
            Your sample report is being generated...
          </span>
        </div>
      )}
      {/* oxlint-disable-next-line react/iframe-missing-sandbox -- This preview is always a PDF, and Chrome's PDF viewer can't render in a sandboxed iframe. See preview/index.tsx. */}
      <iframe
        key={cacheBustedURL}
        src={debouncedPreviewURL}
        title="vulnerability-preview"
        onLoad={onIframeLoad}
        className={cx(previewStyles.iframe, (isLoading || isPending) && previewStyles.iframeLoading)}
      />
    </div>
  );
};
